/**
 * useInstagramFeed
 * Busca os posts do Instagram na edge function instagram-feed (API oficial da
 * Meta, token auto-renovável no banco). Se a função falhar, cai para o feed
 * JSON da Behold (https://feeds.behold.so/<feedId>) como reserva.
 *
 * Histórico: o widget <behold-widget> foi removido em 06/2026 (timeout no
 * cliente derrubava o feed). Em 07/2026 a assinatura da Behold expirou
 * (HTTP 402) e a fonte primária passou a ser a API oficial do Instagram.
 */

import { useEffect, useState } from "react";

const OFFICIAL_FEED_URL =
  "https://zxesxlyctwbclysctpva.supabase.co/functions/v1/instagram-feed";
const FEED_BASE = "https://feeds.behold.so";
const FETCH_TIMEOUT_MS = 6000;

interface BeholdSize {
  width: number;
  height: number;
  mediaUrl: string;
}

interface BeholdRawPost {
  id: string;
  permalink: string;
  caption?: string;
  prunedCaption?: string;
  mediaType?: string;
  isReel?: boolean;
  thumbnailUrl?: string;
  mediaUrl?: string;
  sizes?: Partial<Record<"small" | "medium" | "large" | "full", BeholdSize>>;
}

export interface InstagramPost {
  id: string;
  permalink: string;
  /** URL de imagem da CDN durável da Behold (sempre uma imagem, mesmo para vídeos/reels). */
  imageUrl: string;
  /** Legenda já curta para usar como texto alternativo. */
  caption: string;
  isVideo: boolean;
}

/** Escolhe a melhor URL de imagem disponível (prefere a CDN durável da Behold). */
function pickImage(post: BeholdRawPost): string | null {
  const s = post.sizes;
  return (
    s?.medium?.mediaUrl ||
    s?.large?.mediaUrl ||
    s?.small?.mediaUrl ||
    s?.full?.mediaUrl ||
    post.thumbnailUrl ||
    null
  );
}

async function fetchWithTimeout(url: string): Promise<Response> {
  const controller = new AbortController();
  const timeout = window.setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);
  try {
    const res = await fetch(url, { mode: "cors", signal: controller.signal });
    if (!res.ok) throw new Error(`Feed respondeu ${res.status}`);
    return res;
  } finally {
    window.clearTimeout(timeout);
  }
}

/** Fonte primária: edge function com a API oficial do Instagram. */
async function fetchOfficialFeed(limit: number): Promise<InstagramPost[]> {
  const res = await fetchWithTimeout(`${OFFICIAL_FEED_URL}?limit=${limit}`);
  const data = (await res.json()) as { posts?: InstagramPost[] };
  const posts = Array.isArray(data.posts) ? data.posts : [];
  return posts.filter((p) => !!p.imageUrl && !!p.permalink).slice(0, limit);
}

/** Reserva: feed JSON da Behold. */
async function fetchBeholdFeed(feedId: string, limit: number): Promise<InstagramPost[]> {
  const res = await fetchWithTimeout(`${FEED_BASE}/${feedId}`);
  const data = (await res.json()) as { posts?: BeholdRawPost[] };
  const posts = Array.isArray(data.posts) ? data.posts : [];
  return posts
    .map((p): InstagramPost | null => {
      const imageUrl = pickImage(p);
      if (!imageUrl) return null;
      return {
        id: p.id,
        permalink: p.permalink,
        imageUrl,
        caption: (p.prunedCaption || p.caption || "").trim(),
        isVideo: p.isReel === true || p.mediaType === "VIDEO",
      };
    })
    .filter((p): p is InstagramPost => p !== null)
    .slice(0, limit);
}

async function fetchFeed(feedId: string, limit: number): Promise<InstagramPost[]> {
  try {
    const posts = await fetchOfficialFeed(limit);
    if (posts.length > 0) return posts;
  } catch {
    // função fora do ar ou token pendente: tenta a reserva
  }
  return fetchBeholdFeed(feedId, limit);
}

interface UseInstagramFeedResult {
  posts: InstagramPost[];
  loading: boolean;
  error: boolean;
}

/**
 * @param feedId  ID do feed na Behold.
 * @param limit   Número máximo de posts a retornar.
 * @param enabled Quando false, adia o fetch (usado para lazy-load por viewport).
 */
export function useInstagramFeed(
  feedId: string,
  limit = 8,
  enabled = true,
): UseInstagramFeedResult {
  const [posts, setPosts] = useState<InstagramPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    setLoading(true);
    setError(false);

    fetchFeed(feedId, limit)
      .then((p) => {
        if (cancelled) return;
        if (p.length === 0) setError(true);
        else setPosts(p);
        setLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setError(true);
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [feedId, limit, enabled]);

  return { posts, loading, error };
}
